import React, { FunctionComponent } from 'react';
import { Box, Typography } from '@material-ui/core';
import AccessoriesTotal from '../accessoriesTotal/AccessoriesTotal';
import IAccessoriesCategory from '../../../../../model/accessories/IAccessoriesCategory';
import AccessoriesCategories from '../accessoriesCategories/AccessoriesCategories';
import GenericAccordion from '../../../../../../common/components/presentation/genericAccordion/GenericAccordion';
import IExtendedAccessory from '../../../../../model/accessories/IExtendedAccessory';
import AccessoriesList from '../accessoriesList/AccessoriesList';
import useAccordionStoreState from '../../../../../react/useAccordionStoreState';
import { CustomiseSections } from '../../../../../../common/enum/CustomiseSections';
import SectionStatesWrapper, {
  ISectionStatesProps,
} from '../../../../../../common/components/configuratorStates/SectionStatesWrapper';
import IndicativeNumber from '../../../../../../common/components/indicativeNumber/IndicativeNumber';
import accessoriesSectionStyles from './accessoriesSectionStyles';
import Translation from '../../../../../../common/components/translation/Translation';

export interface IAccessoriesSectionProps extends ISectionStatesProps {
  /**
   * Total number of accessories used to display below the title
   */
  accessoriesTotal: number;
  /**
   * Processed accessories data grouped by categories that contains each corresponding accessory information
   */
  accessoriesCategories?: IAccessoriesCategory[];
  /**
   * Event used to handle logic on selecting the accessory
   */
  onClick: (name: string) => void;
  /**
   * Boolean value that determines the type of blocks to be displayed
   */
  isSearch: boolean;
  /**
   * List of accessories used when searched is active
   */
  searchedAccessories?: IExtendedAccessory[];
  /**
   * List of selected accessories
   */
  selectedAccessories: string[];
  /**
   * Event used to handle scroll to disclaimers section on indicative number click
   */
  onIndicativeClick: (e: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>) => void;
}

const AccessoriesSection: FunctionComponent<IAccessoriesSectionProps> = ({
  accessoriesTotal,
  accessoriesCategories,
  onClick,
  isSearch,
  searchedAccessories,
  selectedAccessories,
  loading,
  error,
  onIndicativeClick,
}) => {
  const { expanded, setExpanded } = useAccordionStoreState(CustomiseSections.ACCESSORIES);
  const styles = accessoriesSectionStyles();

  return (
    <GenericAccordion
      expanded={expanded}
      setExpanded={setExpanded}
      summary={
        <>
          <Typography component={'h3'} variant="sectionTitle">
            <Translation id={'spc.customise.accessories.title'} />
            <IndicativeNumber
              dataTest="accessories"
              onClick={onIndicativeClick}
              displayNumber={<Translation id={'spc.customise.indicative.accessories'} />}
              className={styles.indicativeNumber}
            />
          </Typography>
        </>
      }
      details={
        <Box width={'100%'}>
          <SectionStatesWrapper error={error} loading={loading || !accessoriesCategories?.length}>
            <Box mt={1}>
              <AccessoriesTotal accessoriesTotal={accessoriesTotal} />
            </Box>
            {isSearch ? (
              <AccessoriesList
                selectedAccessories={selectedAccessories}
                accessories={searchedAccessories}
                onClick={onClick}
              />
            ) : (
              <AccessoriesCategories
                selectedAccessories={selectedAccessories}
                accessoriesCategories={accessoriesCategories}
                onClick={onClick}
              />
            )}
          </SectionStatesWrapper>
        </Box>
      }
    />
  );
};

export default AccessoriesSection;
